"use client"

import styled from "@emotion/styled"
import { Box, IconButton } from "@mui/material"
import { Fragment, useRef, useState } from "react"
import {
  Container,
  isVideo,
  PauseOutlineIcon,
  Play2OutlineIcon,
  ResponsiveMedia,
  useIsDesktop,
} from "ui"
import { extractProperty, getUmbracoMediaUrl } from "utils"

const StyledSection = styled.section`
  font-family: var(--narkis) !important;
  * {
    font-family: inherit !important;
  }
  position: relative;
  width: 100%;
  height: 500px;
  overflow: hidden;
  display: flex;
  ${({ theme }) => theme.breakpoints.up("md")} {
  }
  ${({ theme }) => theme.breakpoints.up("xl")} {
    height: 750px;
  }
`

const StyledMedia = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  img,
  video {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 1;
    background-image: linear-gradient(180deg, rgba(5, 41, 70, 0) 0.43%, #052946 99.67%);
    ${(props) => props.theme.breakpoints.up("md")} {
      background-image: linear-gradient(
        ${(props) => (props.theme.direction === "rtl" ? "90deg" : "270deg")},
        rgba(5, 41, 70, 0) 27.53%,
        #052946 99.11%
      );
    }
  }
`

const ControlButton = styled(IconButton)<{ $isPlaying: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  padding: 0;
  background-color: white;
  transition: all 0.3s ease-out;
  color: #1f375d;
  box-shadow: 0 1px 9.2px 0 rgba(0, 0, 0, 0.24);
  & svg {
    transition: all 0.3s ease-out;
    color: inherit;
    fill: transparent;
  }
  &:hover {
    & svg {
      fill: #1f375d;
    }
  }
`

const StyledContent = styled.div`
  position: absolute;
  z-index: 2;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  color: white;

  padding-bottom: 40px;
  ${(props) => props.theme.breakpoints.up("md")} {
    padding-bottom: 60px;
  }
`

const StyledTextContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 24px;
  ${(props) => props.theme.breakpoints.up("md")} {
    gap: 40px;
    margin-left: 25px;
  }
`

const StyledTitle = styled.h1`
  font-family: var(--narkis-condensed) !important;

  margin: 0;
  font-size: 60px;
  font-weight: 400;
  line-height: 53px;
  letter-spacing: -1.2px;

  strong {
    display: block;
    font-weight: 600;
  }

  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 100px;
    line-height: 85px; /* 85% */
    letter-spacing: -2px;
  }
`
const StyledItemList = styled.ul`
  padding: 0;
  margin: 0;
  list-style: none;
  display: flex;
  gap: 20px;
  ${(props) => props.theme.breakpoints.up("md")} {
    align-items: center;
    gap: 60px;
  }
`

const StyledDivider = styled.li`
  margin: 0;
  padding: 0;
  width: 1px;
  background-color: white;
  height: 66px;
  ${(props) => props.theme.breakpoints.up("md")} {
    height: 88px;
  }
`

const StyledItem = styled.li`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 5px;
  ${(props) => props.theme.breakpoints.up("md")} {
    gap: 10px;
  }
`

const StyledItemNumber = styled.data`
  font-family: var(--narkis-condensed) !important;
  font-size: 60px;
  font-weight: 400;
  line-height: 35px;
  letter-spacing: -1.2px;
  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 80px;
    letter-spacing: -1.6px;
    line-height: 45px;
  }
`

const StyledItemLabel = styled.span`
  font-family: var(--narkis) !important;
  font-size: 16px;
  font-weight: 300;
  line-height: 145%;
  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 18px;
    line-height: 150%; /* 27px */
    letter-spacing: -0.36px;
  }
`

type Props = {
  data: any
}

const CareerHero = ({ data }: Props) => {
  const isDesktop = useIsDesktop()
  const title = extractProperty(data, "title")
  const subtitle = extractProperty(data, "subTitle")
  const desktopMedia = extractProperty(data, "mediaDesktop")
  const mobileMedia = extractProperty(data, "mediaMobile")
  const numbersList = (extractProperty(data, "numbersList") || [])
    ?.map((item: any) => ({
      icon: extractProperty(item, "icon"),
      number: extractProperty(item, "number"),
      label: extractProperty(item, "label"),
    }))
    .filter((i: any) => Boolean(i))
  const mediaAlt = title || "Careers Hero Media"

  // video playback state
  const [isPlaying, setIsPlaying] = useState(true)
  const desktopVideoRef = useRef<HTMLVideoElement>(null)
  const mobileVideoRef = useRef<HTMLVideoElement>(null)

  const hasVideoMedia = isDesktop ? isVideo(desktopMedia) : isVideo(mobileMedia)

  const syncPlaybackState = (shouldPlay: boolean) => {
    // eslint-disable-next-line no-extra-semi
    ;[desktopVideoRef.current, mobileVideoRef.current].forEach((video) => {
      if (!video) return
      if (shouldPlay) {
        const playPromise = video.play()
        if (playPromise && typeof playPromise.catch === "function") {
          playPromise.catch(() => undefined)
        }
      } else {
        video.pause()
      }
    })
  }

  const handlePlaybackToggle = () => {
    setIsPlaying((prev) => {
      const next = !prev
      syncPlaybackState(next)
      return next
    })
  }

  return (
    <StyledSection id="careers-herocontent">
      <StyledMedia>
        <ResponsiveMedia
          desktopMedia={getUmbracoMediaUrl(desktopMedia)}
          mobileMedia={getUmbracoMediaUrl(mobileMedia)}
          alt={mediaAlt}
          desktopVideoRef={desktopVideoRef}
          mobileVideoRef={mobileVideoRef}
          priority
        />
      </StyledMedia>
      {hasVideoMedia && (
        <Box
          component={"span"}
          sx={{
            position: { xs: "absolute" },
            top: {
              xs: 24,
              md: "unset",
            },
            bottom: {
              md: 60,
            },
            insetInlineEnd: {
              xs: 20,
              md: 120,
            },
            zIndex: 3,
            display: "flex",
            justifyContent: "center",
          }}
        >
          <ControlButton
            aria-label={isPlaying ? "Pause background video" : "Play background video"}
            onClick={handlePlaybackToggle}
            disableRipple
            $isPlaying={isPlaying}
          >
            {isPlaying ? <PauseOutlineIcon /> : <Play2OutlineIcon />}
          </ControlButton>
        </Box>
      )}
      <StyledContent>
        <Container style={{ margin: "0 auto" }}>
          <StyledTextContainer>
            {title && (
              <StyledTitle>
                {title}
                {subtitle && <strong>{subtitle}</strong>}
              </StyledTitle>
            )}
            {numbersList && numbersList.length > 0 && (
              <StyledItemList>
                {numbersList.map(({ number, label }: any, index: number) => {
                  return (
                    <Fragment key={index}>
                      {index > 0 && <StyledDivider />}
                      <StyledItem>
                        <StyledItemNumber>{number}</StyledItemNumber>
                        <StyledItemLabel>{label}</StyledItemLabel>
                      </StyledItem>
                    </Fragment>
                  )
                })}
              </StyledItemList>
            )}
          </StyledTextContainer>
        </Container>
      </StyledContent>
    </StyledSection>
  )
}
export default CareerHero
